import type { RevenueAnalyticsModel } from '.';
import {
    applySymbolSearch as applySymbolSearchAction,
    clearSymbolSearch as clearSymbolSearchAction,
    initialRevenueAnalyticsState,
    revenueAnalyticsReducer,
    selectProvider as selectProviderAction,
    setPage as setPageAction,
    setRowsPerPage as setRowsPerPageAction,
    setScope as setScopeAction,
    setSymbolSearchInput as setSymbolSearchInputAction,
} from './state';
import type { RevenueAnalyticsAction, RevenueAnalyticsRowsPerPage, RevenueAnalyticsState } from './state';
import { computed, signal } from '@preact/signals-react';
import type { ReadonlySignal } from '@preact/signals-react';
import type { QueryClient, QueryObserverOptions } from '@tanstack/query-core';
import type { Provider } from '@thinkalpha/ui-types/types/admin/lab';
import type { RevenueAnalyticsRow } from 'src/admin/LAB/RevenueAnalytics/types';
import { inject, injectable } from 'src/features/ioc';
import type { QueryObserverModel } from 'src/models/QueryObserverModel';
import { getAllProvidersQuery } from 'src/queries/labProviders';
import { getProviderRevenueAnalyticsQuery, LAB_REVENUE_ANALYTICS } from 'src/queries/labRevenueAnalytics';
import type { ReactBindings } from 'src/types/bindings';

@injectable()
export class RevenueAnalyticsModelImpl implements RevenueAnalyticsModel {
    readonly #disposableStack = new DisposableStack();
    readonly #queryClient: QueryClient;
    readonly #state = signal<RevenueAnalyticsState>(initialRevenueAnalyticsState);

    #dispatch(action: RevenueAnalyticsAction): void {
        this.#state.value = revenueAnalyticsReducer(this.#state.value, action);
    }

    readonly #providersQuery: QueryObserverModel<Provider[], Error, Provider[]>;
    readonly #analyticsQuery: QueryObserverModel<RevenueAnalyticsRow[], Error, RevenueAnalyticsRow[]>;

    readonly providers: ReadonlySignal<ReadonlyArray<Provider>>;
    readonly selectedProviderId: ReadonlySignal<string> = computed(() => this.#state.value.selectedProviderId);
    readonly selectedProvider: ReadonlySignal<Provider | null>;
    readonly rows: ReadonlySignal<ReadonlyArray<RevenueAnalyticsRow>>;
    readonly isLoading: ReadonlySignal<boolean>;
    readonly isError: ReadonlySignal<boolean>;
    readonly isFetching: ReadonlySignal<boolean>;
    readonly lastUpdatedAt: ReadonlySignal<number>;
    readonly symbolSearchInput: ReadonlySignal<string> = computed(() => this.#state.value.symbolSearchInput);
    readonly isSearchActive: ReadonlySignal<boolean> = computed(() => this.#state.value.appliedSymbolSearch !== '');
    readonly page: ReadonlySignal<number> = computed(() => this.#state.value.page);
    readonly rowsPerPage: ReadonlySignal<RevenueAnalyticsRowsPerPage> = computed(() => this.#state.value.rowsPerPage);
    readonly hasPreviousPage: ReadonlySignal<boolean>;
    readonly hasNextPage: ReadonlySignal<boolean>;
    readonly pageStartIndex: ReadonlySignal<number>;
    readonly pageEndIndex: ReadonlySignal<number>;

    constructor(
        @inject('QueryObserverModelFactory') queryObserverModelFactory: ReactBindings['QueryObserverModelFactory'],
        @inject('QueryClient') queryClient: QueryClient,
    ) {
        this.#queryClient = queryClient;

        const providersOptions = computed<QueryObserverOptions<Provider[], Error, Provider[]>>(() => {
            const { firmId, superUserMode } = this.#state.value;
            return {
                ...getAllProvidersQuery(firmId),
                // Mirrors `useAllProviders`: superusers can list without a firm.
                enabled: superUserMode || Boolean(firmId),
            };
        });
        this.#providersQuery = queryObserverModelFactory<Provider[], Error, Provider[]>(providersOptions);
        this.#disposableStack.use(this.#providersQuery);

        this.providers = computed(() => this.#providersQuery.signal.value.data ?? []);
        this.selectedProvider = computed(() => {
            const id = this.#state.value.selectedProviderId;
            if (!id) return null;
            return this.providers.value.find((provider) => provider.id === id) ?? null;
        });

        const analyticsOptions = computed<QueryObserverOptions<RevenueAnalyticsRow[], Error, RevenueAnalyticsRow[]>>(
            () => {
                const provider = this.selectedProvider.value ?? undefined;
                const s = this.#state.value;
                // A committed search is answered by the backend `symbol`
                // param; it returns the single match, so paging is bypassed.
                if (s.appliedSymbolSearch) {
                    return getProviderRevenueAnalyticsQuery(provider, { symbol: s.appliedSymbolSearch });
                }
                return getProviderRevenueAnalyticsQuery(provider, {
                    skip: (s.page - 1) * s.rowsPerPage,
                    limit: s.rowsPerPage,
                });
            },
        );
        this.#analyticsQuery = queryObserverModelFactory<RevenueAnalyticsRow[], Error, RevenueAnalyticsRow[]>(
            analyticsOptions,
        );
        this.#disposableStack.use(this.#analyticsQuery);

        this.rows = computed(() => this.#analyticsQuery.signal.value.data ?? []);
        // `isLoading` gates on a selected provider so the "select a provider"
        // empty state shows before one is chosen (the query is disabled then).
        this.isLoading = computed(
            () => Boolean(this.selectedProvider.value) && this.#analyticsQuery.signal.value.isLoading,
        );
        this.isError = computed(() => this.#analyticsQuery.signal.value.isError);
        this.isFetching = computed(() => this.#analyticsQuery.signal.value.isFetching);
        this.lastUpdatedAt = computed(() => this.#analyticsQuery.signal.value.dataUpdatedAt);

        this.hasPreviousPage = computed(() => !this.isSearchActive.value && this.#state.value.page > 1);
        // No total count from the backend yet (a follow-up will add it): a full
        // page implies there may be more, so Next is enabled only when the
        // current page came back full.
        this.hasNextPage = computed(
            () => !this.isSearchActive.value && this.rows.value.length === this.#state.value.rowsPerPage,
        );
        this.pageStartIndex = computed(() => {
            if (this.rows.value.length === 0) return 0;
            if (this.isSearchActive.value) return 1;
            const s = this.#state.value;
            return (s.page - 1) * s.rowsPerPage + 1;
        });
        this.pageEndIndex = computed(() => {
            const count = this.rows.value.length;
            if (count === 0) return 0;
            if (this.isSearchActive.value) return count;
            const s = this.#state.value;
            return (s.page - 1) * s.rowsPerPage + count;
        });
    }

    setScope(firmId: string | undefined, superUserMode: boolean): void {
        this.#dispatch(setScopeAction(firmId, superUserMode));
    }

    selectProvider(providerId: string): void {
        this.#dispatch(selectProviderAction(providerId));
    }

    setRowsPerPage(rowsPerPage: RevenueAnalyticsRowsPerPage): void {
        this.#dispatch(setRowsPerPageAction(rowsPerPage));
    }

    goToPreviousPage(): void {
        if (this.hasPreviousPage.value) this.#dispatch(setPageAction(this.#state.value.page - 1));
    }

    goToNextPage(): void {
        if (this.hasNextPage.value) this.#dispatch(setPageAction(this.#state.value.page + 1));
    }

    setSymbolSearchInput(value: string): void {
        this.#dispatch(setSymbolSearchInputAction(value));
    }

    applySymbolSearch(): void {
        this.#dispatch(applySymbolSearchAction());
    }

    clearSymbolSearch(): void {
        this.#dispatch(clearSymbolSearchAction());
    }

    async refresh(): Promise<void> {
        const providerId = this.#state.value.selectedProviderId;
        await this.#queryClient.invalidateQueries({ queryKey: [LAB_REVENUE_ANALYTICS, providerId || null] });
    }

    [Symbol.dispose](): void {
        this.#disposableStack.dispose();
    }
}
